/**
 * ②bis Votre signature (B-17, tranche par le porteur le 19/09/2026).
 *
 * Trois choix : son nom, un trace, une image. Le nom est PRESELECTIONNE -
 * c'est le visuel d'avant B-17, et un signataire presse doit pouvoir
 * avancer d'un clic.
 *
 * Ce que cet ecran n'est pas : l'acte de signature. Celui-ci reste la
 * validation du code recu. Le visuel ne fait que le representer, et la
 * note en bas de l'ecran le dit au signataire plutot que de le laisser
 * croire que dessiner l'engage.
 *
 * Le trace est capture comme des POINTS, jamais comme une image : ce sont
 * eux qui partent au serveur, qui les redessine en traits dans le PDF.
 * Resultat mesure : 1 815 octets contre 17 116 pour une image, et un
 * rendu net a n'importe quel zoom.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, postSignature, type MethodeSignature, type SignerView, type Trait } from "../api";
import type { t } from "../i18n";
import { ErrorPanel } from "../components/ErrorPanel";

const TAILLE_MAX_IMAGE = 4 * 1024 * 1024;

interface Props {
  token: string;
  view: SignerView;
  L: ReturnType<typeof t>;
  onDone: () => void;
  /** Etape du panneau (visionneuse en echec) : refuser de signer. */
  onDecline?: () => void;
  /** Fenetre « Votre signature » (Franck, 20/10/2026) : « Annuler » la ferme,
   *  ce n'est pas un refus de signer. */
  onAnnuler?: () => void;
}

export function Signature({ token, view, L, onDone, onDecline, onAnnuler }: Props) {
  const [methode, setMethode] = useState<MethodeSignature>("nom");
  const [traits, setTraits] = useState<Trait[]>([]);
  const [image, setImage] = useState<File | null>(null);
  const [apercuImage, setApercuImage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [manque, setManque] = useState<string | null>(null);

  const nom = view.signer_name || view.signer_name === "" ? view.signer_name : null;
  // Meme regle que le PDF (pdf_enveloppe.nom_manuscrit) : un nom saisi tout
  // en capitales est trace avec une majuscule par mot, sinon illisible.
  const lettres = (nom || "").replace(/[^\p{L}]/gu, "");
  const affiche = lettres.length > 1 && lettres === lettres.toUpperCase()
    ? (nom || "").toLowerCase().replace(/(^|[\s-])(\p{L})/gu, (_, sep: string, c: string) => sep + c.toUpperCase())
    : nom || "";
  const manuscrit = view.nom_manuscrit_possible !== false;

  const pret =
    methode === "nom" || (methode === "trace" && traits.length > 0) || (methode === "image" && image !== null);

  const envoyer = async () => {
    if (!pret) {
      setManque(methode === "trace" ? L.signatureDrawRequired : L.signatureImageRequired);
      return;
    }
    setSubmitting(true);
    setError(null);
    setManque(null);
    try {
      await postSignature(token, methode, {
        trace: methode === "trace" ? traits : undefined,
        image: methode === "image" && image ? image : undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError("server", 0, null));
    } finally {
      setSubmitting(false);
    }
  };

  const choisirImage = (fichier: File | null) => {
    setImage(fichier);
    setApercuImage((ancien) => {
      if (ancien) URL.revokeObjectURL(ancien);
      return fichier ? URL.createObjectURL(fichier) : null;
    });
  };

  return (
    <section className="card stack">
      <div>
        <h1 className="card__title">{L.signatureTitle}</h1>
        <p className="card__lead">{L.signatureLead}</p>
      </div>

      <div className="sig-choix">
        <Option
          actif={methode === "nom"}
          titre={L.signatureByName}
          aide={manuscrit ? L.signatureByNameHelp : L.signatureByNamePlain}
          onChoisir={() => setMethode("nom")}
        />
        <Option
          actif={methode === "trace"}
          titre={L.signatureByDraw}
          aide={L.signatureByDrawHelp}
          onChoisir={() => setMethode("trace")}
        />
        <Option
          actif={methode === "image"}
          titre={L.signatureByImage}
          aide={L.signatureByImageHelp}
          onChoisir={() => setMethode("image")}
        />
      </div>

      {methode === "nom" && (
        <div className="sig-apercu">
          {/* La police du document, pas une autre : l'apercu doit montrer
              ce que le PDF portera. Quand elle ne couvre pas le nom, le
              serveur le dit et on affiche la police ordinaire - celle sur
              laquelle le document se rabattra. */}
          <span className={manuscrit ? "sig-apercu__nom" : "sig-apercu__nom sig-apercu__nom--simple"}>
            {affiche}
          </span>
        </div>
      )}

      {methode === "trace" && (
        <ZoneDeTrace traits={traits} onChange={setTraits} L={L} />
      )}

      {methode === "image" && (
        <div className="stack">
          <div className="actions" style={{ margin: 0 }}>
            <label className="btn btn--secondary" style={{ cursor: "pointer" }}>
              {L.signatureChoose}
              <input
                type="file"
                accept="image/png,image/jpeg"
                style={{ display: "none" }}
                onChange={(e) => {
                  const fichier = e.target.files?.[0] ?? null;
                  // Borne aussi cote site : inutile de faire televerser
                  // quatre megaoctets pour les faire refuser a l'arrivee.
                  if (fichier && fichier.size > TAILLE_MAX_IMAGE) {
                    setManque(L.signatureByImageHelp);
                    choisirImage(null);
                    return;
                  }
                  setManque(null);
                  choisirImage(fichier);
                }}
              />
            </label>
          </div>
          {apercuImage && (
            <div className="sig-apercu">
              <img src={apercuImage} alt="" />
            </div>
          )}
        </div>
      )}

      {manque && (
        <p className="alert alert--error" role="alert">
          {manque}
        </p>
      )}
      {error && <ErrorPanel error={error} L={L} />}

      <p className="muted">{view.cle_acces ? L.signatureNoteCle : L.signatureNote}</p>

      <div className="actions actions--split">
        {/* DESIGN.md : annuler a gauche, valider a droite. */}
        {onAnnuler && <button type="button" className="btn btn--secondary" onClick={onAnnuler}>{L.annuler}</button>}
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => void envoyer()}
          disabled={submitting}
        >
          {L.signatureSubmit}
        </button>
        {!onAnnuler && <button type="button" className="btn btn--danger" onClick={onDecline}>{L.refuse}</button>}
      </div>
    </section>
  );
}

function Option({
  actif,
  titre,
  aide,
  onChoisir,
}: {
  actif: boolean;
  titre: string;
  aide: string;
  onChoisir: () => void;
}) {
  return (
    <label className={actif ? "sig-option sig-option--active" : "sig-option"}>
      <input type="radio" name="methode-signature" checked={actif} onChange={onChoisir} />
      <span>
        <span className="sig-option__titre">{titre}</span>
        <br />
        <span className="muted">{aide}</span>
      </span>
    </label>
  );
}

/**
 * Zone de trace.
 *
 * Les points sont enregistres en FRACTION de la zone (0..1) et non en
 * pixels : la zone n'a ni la taille ni les proportions du cadre ou la
 * signature atterrira, et le serveur ajuste le trace a l'encre plutot
 * qu'aux bords. Un trace capture en pixels serait deforme.
 */
function ZoneDeTrace({
  traits,
  onChange,
  L,
}: {
  traits: Trait[];
  onChange: (traits: Trait[]) => void;
  L: ReturnType<typeof t>;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Le doigt qui dessine en ce moment. Sert a ignorer un SECOND pointeur
  // simultane (une paume posee a cote), jamais a bloquer un nouveau trait.
  const pointeur = useRef<number | null>(null);
  // Un evenement « pointeur » a-t-il DEJA ete recu sur ce cadre ?
  //
  // Un lien de signature arrive par e-mail : il s'ouvre donc souvent dans
  // le navigateur integre a une application de messagerie, et certains
  // n'emettent aucun evenement « pointeur ». Detecter la seule PRESENCE de
  // l'API ne suffirait pas - un navigateur peut l'exposer sans jamais rien
  // emettre. On branche donc les trois sources, et c'est celle qui parle
  // qui gagne : le tactile et la souris ne servent que si le pointeur
  // s'est tu.
  //
  // Mesure sur l'app Yahoo Mail (iPhone, 20/09/2026) : le pointeur y
  // fonctionne (12 appuis, 593 deplacements) et le tactile reste donc
  // inhibe. Le repli n'a pas servi la - il protege les navigateurs qui,
  // eux, n'envoient rien.
  const pointeurVu = useRef(false);
  // Miroir des traits, lisible IMMEDIATEMENT.
  //
  // `traits` est la valeur du DERNIER RENDU : plusieurs evenements de
  // deplacement traites avant que React ne re-rende liraient tous la meme
  // valeur et s'ecraseraient l'un l'autre - mesure : un trait de trois
  // points n'en gardait qu'un. Le miroir est mis a jour dans la foulee de
  // chaque evenement, donc le suivant voit bien le point precedent.
  const traitsRef = useRef(traits);
  traitsRef.current = traits;

  // ⚠️ DEUX CORRECTIFS POSES ENSEMBLE le 20/09/2026, et il faut le dire :
  // on ne saura pas lequel des deux a suffi. Le porteur a passe la journee
  // sur ce defaut, la priorite est qu'il signe.
  //
  // (1) UN ECOUTEUR TACTILE NON PASSIF, pose a la main.
  //
  // React attache ses gestionnaires tactiles en mode PASSIF : le
  // `preventDefault()` ecrit dans onTouchMove plus bas est purement
  // IGNORE - React le signale meme en console. Or c'est lui qui empeche
  // WebKit de reinterpreter un glisse en defilement de page. Quand WebKit
  // le reinterprete, il met fin au pointeur au bout d'un ou deux
  // evenements : ce qui donne « un seul point », puis « deux points » une
  // fois la fin sur lostpointercapture retiree. Le symptome colle
  // exactement.
  //
  // `touch-action: none` sur le cadre devrait deja suffire. Manifestement
  // non dans le navigateur integre de Yahoo Mail sur iOS - et c'est ce
  // navigateur-la qui compte, puisque le lien arrive par courriel.
  //
  // Seulement `touchmove` : `preventDefault` sur `touchstart` empeche
  // aussi la synthese d'autres evenements, et on n'a pas besoin de ca.
  //
  // (2) DEUX ECOUTEURS SUR LA FENETRE, plus bas. Ils ne comptent rien -
  // ils ne sont la que parce que leur presence correlait, TROIS FOIS, avec
  // un trace qui fonctionne. Je n'ai pas elucide par quel mecanisme.
  useEffect(() => {
    const cadre = canvasRef.current;
    if (!cadre) return;
    const bloquer = (e: TouchEvent) => e.preventDefault();
    cadre.addEventListener("touchmove", bloquer, { passive: false });
    // Sans effet propre, mais leur presence a toujours accompagne un
    // trace qui marche (mesure les 20/09 a 15h37, 15h48 et 15h58).
    const rien = () => undefined;
    window.addEventListener("touchstart", rien, { capture: true, passive: true });
    window.addEventListener("pointerdown", rien, { capture: true, passive: true });
    return () => {
      cadre.removeEventListener("touchmove", bloquer);
      window.removeEventListener("touchstart", rien, { capture: true });
      window.removeEventListener("pointerdown", rien, { capture: true });
    };
  }, []);

  const redessiner = useCallback((tous: Trait[]) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (canvas.width !== Math.round(rect.width * dpr)) {
      canvas.width = Math.round(rect.width * dpr);
      canvas.height = Math.round(rect.height * dpr);
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = Math.max(2, 2.5 * dpr);
    ctx.strokeStyle = "#16324f";
    // Memes bouts ronds qu'a l'impression : ce que le signataire voit doit
    // ressembler a ce que le document portera.
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const trait of tous) {
      const [depart, ...suite] = trait;
      if (!depart) continue;
      ctx.beginPath();
      ctx.moveTo(depart.x * canvas.width, depart.y * canvas.height);
      // Un appui sans mouvement : un segment minuscule, que le bout rond
      // transforme en point visible - comme a l'impression.
      if (suite.length === 0) ctx.lineTo(depart.x * canvas.width + 0.1, depart.y * canvas.height);
      for (const point of suite) ctx.lineTo(point.x * canvas.width, point.y * canvas.height);
      ctx.stroke();
    }
  }, []);

  /** Les trois gestes elementaires. Toute la logique de trace vit ici, une
   *  seule fois, quelle que soit la source des evenements. */
  const debut = (x: number, y: number, id: number) => {
    pointeur.current = id;
    appliquer([...traitsRef.current, [borner(x, y)]]);
  };

  const deplacer = (x: number, y: number, id: number) => {
    if (pointeur.current !== id) return;
    const courant = traitsRef.current;
    const dernier = courant[courant.length - 1];
    if (!dernier) return;
    appliquer([...courant.slice(0, -1), [...dernier, borner(x, y)]]);
  };

  // Les fins ne valident rien - tout est deja enregistre. Elles relachent
  // seulement le doigt, pour que l'appui suivant soit compris comme un
  // nouveau trait. Mesure sur iPhone : `pointercancel` n'arrive JAMAIS.
  //
  // ⚠️ `lostpointercapture` N'EST PAS UNE FIN DE GESTE, et le traiter comme
  // telle a coute une panne le 20/09/2026 : le signataire n'obtenait qu'UN
  // SEUL POINT par trait. L'evenement dit « la capture a ete relachee », pas
  // « le doigt est parti ». Il se trouvait arriver en fin de geste la nuit
  // precedente, d'ou la confusion ; selon le contexte, WebKit la relache
  // AUSSITOT APRES L'APPUI - et le trait etait alors clos avant son premier
  // deplacement, tous les suivants etant rejetes par le garde-fou du doigt
  // unique.
  //
  // Le garde-fou qui l'avait fait ajouter n'existe plus : un trait jamais
  // referme ne bloque plus rien, puisque TOUT nouvel appui commence un
  // nouveau trait. On ne depend donc d'aucun evenement de fin, et perdre la
  // capture n'empeche pas de dessiner DANS le cadre - elle ne sert qu'a
  // poursuivre un trait qui en sort, ce que le code appelle deja un confort.
  //
  // La regle generale, pour la prochaine fois : ne jamais deduire le SENS
  // d'un evenement de l'instant ou on l'a vu arriver une fois.
  const fin = () => {
    pointeur.current = null;
  };

  /** Enregistre et dessine d'un seul geste, miroir compris. */
  const appliquer = (nouveaux: Trait[]) => {
    traitsRef.current = nouveaux;
    onChange(nouveaux);
    redessiner(nouveaux);
  };

  /** Coordonnees ecran -> fraction du cadre (0..1), bornees. */
  const borner = (clientX: number, clientY: number) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
    return {
      x: Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1),
      y: Math.min(Math.max((clientY - rect.top) / rect.height, 0), 1),
    };
  };

  return (
    <div className="stack">
      <p className="muted" style={{ margin: 0 }}>
        {L.signatureDrawHere}
      </p>
      <canvas
        ref={(el) => {
          canvasRef.current = el;
          if (el) redessiner(traits);
        }}
        className="sig-canvas"
        /**
         * Le trait vit dans l'ETAT des le premier point, et non dans une
         * variable qu'un evenement de fin viendrait valider.
         *
         * Pourquoi (20/09/2026, deuxieme correctif). Le premier tentait de
         * conserver le trait sur `pointercancel` : vrai defaut, mais pas
         * celui du porteur, qui a retrouve le meme comportement sur son
         * iPhone AVEC le nouveau code - verifie dans le journal nginx, ce
         * n'etait pas un cache. Le symptome « apres le premier trait plus
         * rien, meme apres Effacer » s'explique si AUCUN evenement de fin
         * n'arrive : le trait n'est jamais valide, et le garde-fou « un
         * seul doigt a la fois » reste bloque pour toujours.
         *
         * Plutot que de deviner ce que tel navigateur envoie, on ne
         * dependant plus d'aucun evenement de fin : ce qui est dessine est
         * enregistre en avancant. Les fins ne font plus que relacher le
         * pointeur, et un nouvel appui commence toujours un nouveau trait -
         * meme si le precedent n'a jamais ete referme.
         */
        // --- source 1 : evenements « pointeur » ---
        onPointerDown={(e) => {
          pointeurVu.current = true;
          if (pointeur.current !== null && e.isPrimary === false) return;
          e.preventDefault();
          try {
            e.currentTarget.setPointerCapture(e.pointerId);
          } catch {
            // La capture est un confort (poursuivre hors du cadre), pas une
            // condition pour dessiner : si elle leve, on dessine quand meme.
          }
          debut(e.clientX, e.clientY, e.pointerId);
        }}
        onPointerMove={(e) => deplacer(e.clientX, e.clientY, e.pointerId)}
        onPointerUp={fin}
        onPointerCancel={fin}
        // Et surtout PAS de onLostPointerCapture : son absence est le
        // correctif, pas un oubli. Voir le commentaire de `fin`.
        // --- source 2 : tactile, seulement si le pointeur ne parle pas ---
        onTouchStart={(e) => {
          if (pointeurVu.current) return;
          const t = e.changedTouches[0];
          if (!t) return;
          e.preventDefault();
          debut(t.clientX, t.clientY, t.identifier);
        }}
        onTouchMove={(e) => {
          if (pointeurVu.current) return;
          const t = e.changedTouches[0];
          if (!t) return;
          e.preventDefault();
          deplacer(t.clientX, t.clientY, t.identifier);
        }}
        onTouchEnd={() => {
          if (!pointeurVu.current) fin();
        }}
        onTouchCancel={() => {
          if (!pointeurVu.current) fin();
        }}
        // --- source 3 : souris, pour un navigateur sans pointeur ni tactile ---
        onMouseDown={(e) => {
          if (pointeurVu.current) return;
          debut(e.clientX, e.clientY, -1);
        }}
        onMouseMove={(e) => {
          if (pointeurVu.current || pointeur.current !== -1) return;
          deplacer(e.clientX, e.clientY, -1);
        }}
        onMouseUp={() => {
          if (!pointeurVu.current) fin();
        }}
        onMouseLeave={() => {
          if (!pointeurVu.current) fin();
        }}
      />
      <div className="actions" style={{ margin: 0 }}>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={() => {
            pointeur.current = null;
            appliquer([]);
          }}
          disabled={traits.length === 0}
        >
          {L.signatureClear}
        </button>
      </div>
    </div>
  );
}
